/**
 * Image Scaling and Aspect Ratio Utilities
 *
 * Provides consistent image scaling and aspect ratio handling across the application.
 * Based on a three-tier sizing system: container constraints, aspect ratio buckets,
 * and size classifications.
 */

export interface ImageDimensions {
  width: number
  height: number
}

export interface ContainerConstraints {
  maxWidth: number
  maxHeight: number
  minSize: number
}

export type AspectRatioBucket =
  | 'portrait'
  | 'square'
  | 'landscape'
  | 'panoramic'
  | 'ultra-wide'
  | 'ultra-tall'
  | 'unknown'
export type SizeClass = 'tiny' | 'small' | 'medium' | 'large' | 'unknown'
export type ViewContext = 'library' | 'modal' | 'fullscreen' | 'embedded'

/**
 * Determine aspect ratio bucket based on width/height ratio
 * @param width
 * @param height
 */
export function getAspectRatioBucket(width: number, height: number): AspectRatioBucket {
  // Handle invalid dimensions
  if (!width || !height || width <= 0 || height <= 0 || !isFinite(width) || !isFinite(height)) {
    return 'unknown'
  }

  const aspectRatio = width / height

  // Ultra-tall (< 0.5)
  if (aspectRatio < 0.5) {
    return 'ultra-tall'
  }

  // Portrait (0.5 - 0.75)
  if (aspectRatio <= 0.75) {
    return 'portrait'
  }

  // Square (0.75 - 1.33)
  if (aspectRatio <= 1.33) {
    return 'square'
  }

  // Landscape (1.33 - 2.5)
  if (aspectRatio <= 2.5) {
    return 'landscape'
  }

  // Panoramic (2.5 - 4.0)
  if (aspectRatio <= 4.0) {
    return 'panoramic'
  }

  // Ultra-wide (> 4.0)
  return 'ultra-wide'
}

/**
 * Classify image size based on largest dimension
 * @param width
 * @param height
 */
export function getSizeClass(width: number, height: number): SizeClass {
  if (!width || !height || width <= 0 || height <= 0 || !isFinite(width) || !isFinite(height)) {
    return 'unknown'
  }

  const maxDimension = Math.max(width, height)

  if (maxDimension < 200) return 'tiny'
  if (maxDimension < 400) return 'small'
  if (maxDimension < 800) return 'medium'
  return 'large'
}

/**
 * Get container constraints based on viewport context
 * @param context
 */
export function getContainerConstraints(context: ViewContext): ContainerConstraints {
  const constraints = {
    library: {
      maxWidth: 400,
      maxHeight: 300,
      minSize: 150,
    },
    modal: {
      maxWidth: 1200,
      maxHeight: Math.min(window.innerHeight * 0.7, 800),
      minSize: 200,
    },
    fullscreen: {
      maxWidth: window.innerWidth * 0.95,
      maxHeight: window.innerHeight * 0.95,
      minSize: 200,
    },
    embedded: {
      maxWidth: 600,
      maxHeight: 400,
      minSize: 150,
    },
  }

  return constraints[context] || constraints.embedded
}

/**
 * Calculate optimal display dimensions within container constraints
 * @param imageDimensions
 * @param containerConstraints
 * @param objectFit
 */
export function fitToContainer(
  imageDimensions: ImageDimensions,
  containerConstraints: ContainerConstraints,
  objectFit: 'contain' | 'cover' | 'none' = 'contain',
): ImageDimensions {
  const { width: imgWidth, height: imgHeight } = imageDimensions
  const { maxWidth, maxHeight, minSize } = containerConstraints

  // Handle tiny images - don't scale up
  if (getSizeClass(imgWidth, imgHeight) === 'tiny' || objectFit === 'none') {
    return {
      width: Math.max(imgWidth, minSize),
      height: Math.max(imgHeight, minSize),
    }
  }

  // Calculate scaling to fit within container
  const scaleX = maxWidth / imgWidth
  const scaleY = maxHeight / imgHeight

  let scale: number
  if (objectFit === 'cover') {
    // Scale to fill container (may crop)
    scale = Math.max(scaleX, scaleY)
  } else {
    // Scale to fit within container (contain)
    scale = Math.min(scaleX, scaleY, 1) // Don't scale up beyond original size
  }

  return {
    width: Math.round(imgWidth * scale),
    height: Math.round(imgHeight * scale),
  }
}

/**
 * Get CSS classes for image display based on dimensions and context
 * @param imageDimensions
 * @param context
 * @param aspectRatioBucket
 * @param sizeClass
 */
export function getImageClasses(
  imageDimensions: ImageDimensions | null,
  context: ViewContext,
  aspectRatioBucket?: AspectRatioBucket,
  sizeClass?: SizeClass,
): string[] {
  const classes: string[] = []

  // Add context class
  classes.push(`context-${context}`)

  // Add aspect ratio class
  if (aspectRatioBucket) {
    classes.push(`aspect-${aspectRatioBucket}`)
  }

  // Add size class
  if (sizeClass) {
    classes.push(`size-${sizeClass}`)
  }

  // Add object-fit strategy class
  if (imageDimensions) {
    const bucket =
      aspectRatioBucket || getAspectRatioBucket(imageDimensions.width, imageDimensions.height)
    const size = sizeClass || getSizeClass(imageDimensions.width, imageDimensions.height)

    // Tiny images should not be scaled up
    if (size === 'tiny') {
      classes.push('object-fit-none')
    }
    // Panoramic and ultra-wide images might benefit from cover in some contexts
    else if ((bucket === 'panoramic' || bucket === 'ultra-wide') && context === 'library') {
      classes.push('object-fit-cover')
    }
    // Default to contain
    else {
      classes.push('object-fit-contain')
    }
  }

  return classes
}

/**
 * Get skeleton/placeholder aspect ratio for loading states
 * @param aspectRatioBucket
 */
export function getPlaceholderAspectRatio(aspectRatioBucket?: AspectRatioBucket): number {
  const ratios = {
    'ultra-tall': 0.4, // 2:5
    portrait: 0.75, // 3:4
    square: 1.0, // 1:1
    landscape: 1.78, // 16:9
    panoramic: 2.5, // 5:2
    'ultra-wide': 4.0, // 4:1
    unknown: 1.0, // 1:1 default
  }

  return ratios[aspectRatioBucket || 'unknown']
}

/**
 * Preload image to get dimensions without causing layout shift
 * @param url
 */
export function preloadImage(url: string): Promise<ImageDimensions> {
  return new Promise((resolve, reject) => {
    const img = new Image()

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${url}`))
    }

    img.src = url
  })
}

/**
 * Detect if browser supports modern image formats
 */
export function getImageFormatSupport() {
  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1

  return {
    webp: canvas.toDataURL('image/webp').indexOf('webp') !== -1,
    avif: canvas.toDataURL('image/avif').indexOf('avif') !== -1,
  }
}
